import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { SectionAction, SectionActionType, SectionContent, SectionTitleConfig, TemplateType } from '../types';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { desanitizeObject } from '../../../utils/sanitization.utils';
import { GzLabeledContentWebComponent } from '../../gzLabeledContent';
import { htmlEncode } from '../../../utils/escape.utils';
import { translateFn } from '../../../utils/translator.helper';

export class GzGenericDetailsSection extends LitElement {
  @property({ type: String })
  sectionTitle: string;

  @property({ type: Object })
  sectionTitleConfig?: SectionTitleConfig;

  @property({ type: Array })
  sectionContents: Array<SectionContent>;

  @property({ type: Array })
  sectionActions: Array<SectionAction> = [];

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="wrapper">
        <gz-title
          class="section-title"
          fontSize="${this.sectionTitleConfig?.fontSize ?? FontSizes.PARAGRAPH_11}"
          fontWeight="${this.sectionTitleConfig?.fontWeight ?? FontWeights.SEMIBOLD}"
          >${this.sectionTitle}</gz-title
        >
      </div>

      <div class="content">${this.renderSectionContents()}</div>
      <div id="sectionFooter">${this.renderSectionFooter()}</div>
    `;
  }

  private renderSectionContents() {
    return html`${this.sectionContents.map((sectionContent: SectionContent) =>
      this.renderSectionContent(sectionContent)
    )}`;
  }

  private renderSectionContent(sectionContent: SectionContent) {
    const content =
      sectionContent.templateType === TemplateType.ELEMENT ? sectionContent : desanitizeObject(sectionContent);
    const gzLabeledContent = new GzLabeledContentWebComponent();
    gzLabeledContent.label = content.label;
    gzLabeledContent.clampLine = !!sectionContent.clampLine;

    if (sectionContent.templateType === TemplateType.ELEMENT) {
      gzLabeledContent.appendChild(sectionContent.content as LitElement);
    } else {
      gzLabeledContent.innerHTML = this.renderContent(content);
    }
    return gzLabeledContent;
  }

  private renderContent(field: SectionContent) {
    const content = field.templateType === TemplateType.STRING ? field.content : htmlEncode(field.content);
    return field.canCopy
      ? `<gz-copy-to-clipboard showIconOnHover confirmationMessage="${translateFn(
          'Copied'
        )}"><span>${content}</span></gz-copy-to-clipboard>`
      : `${content}`;
  }

  private renderSectionFooter() {
    return html`${this.sectionActions.map((sectionAction: SectionAction) => this.renderSectionAction(sectionAction))}`;
  }

  private renderSectionAction(sectionAction: SectionAction) {
    let sectionActionContent;

    switch (sectionAction.type) {
      case SectionActionType.GZ_BUTTON:
        sectionActionContent = html`<gz-button
          @click="${this.emitEvent.bind(this, sectionAction)}"
          class="action-footer-item"
          .id="${sectionAction.itemConfig.id}"
          .type="${sectionAction.itemConfig.type}"
          .color="${sectionAction.itemConfig.color}"
          .buttonHeight="${sectionAction.itemConfig.buttonHeight}"
          .buttonFontSize="${sectionAction.itemConfig.buttonFontSize}"
          .disabled="${sectionAction.itemConfig.disabled}"
        >
          ${sectionAction.label}
        </gz-button>`;
        break;
      default:
        throw new Error(`Unknown section type ${sectionAction.type}`);
    }

    if (sectionAction.itemConfig.tooltip) {
      return html`<gz-tooltip class="action-footer-item" tooltipText="${sectionAction.itemConfig.tooltip}"
        >${sectionActionContent}</gz-tooltip
      >`;
    }

    return html`${sectionActionContent}`;
  }

  private emitEvent(sectionAction: SectionAction) {
    const event = new CustomEvent(sectionAction.eventName, {
      detail: sectionAction.eventData,
      bubbles: true,
      composed: true,
      cancelable: true,
    });

    this.dispatchEvent(event);
  }
}
